import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** ② in re-find mode (spec 2026-10-31 §3): search terms are editable here —
 *  before, they lived only at ① — and the seed ③ blamed is named. The search
 *  queries come from the terms alone, so the button waits for new terms:
 *  the same terms find the same seeds (copy: Brad, 2026-11-01). */
export function RefindBanner({ terms: initial, searched, busy, error, blame, onFind, onUntick }: {
  terms: string;
  /** Terms of the last search; Find seeds again needs different ones. */
  searched: string;
  busy: boolean;
  error: string | null;
  blame: { label: string; offTopic: number; total: number } | null;
  onFind: (terms: string) => void;
  /** null: the blamed seed isn't ticked, or the drag line already names it. */
  onUntick: (() => void) | null;
}) {
  const [terms, setTerms] = useState(initial);
  const boxRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Arriving from ③: bring the banner into view and put the cursor in the terms.
  useEffect(() => {
    boxRef.current?.scrollIntoView?.({ block: "nearest" });
    inputRef.current?.focus({ preventScroll: true });
  }, []);
  const same = terms.trim() === searched.trim();
  return (
    <section ref={boxRef} aria-label="Find different seeds" className="flex flex-col gap-3 rounded-lg border-2 border-primary bg-primary/10 p-4">
      <span className="flex items-center gap-2 font-medium">
        <Search className="size-4 text-primary" aria-hidden />
        Find different seeds
      </span>
      <div className="flex flex-col gap-1 text-sm text-muted-foreground">
        <p>Seed papers come from searching for these terms. Adjust the terms to specify your subject more precisely: an object, a method, a key author, etc.</p>
        <p>Your question is then used to pick the most relevant seed papers, and again later to grade trawled papers. If necessary, you can refine the question in the next step.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="refind-terms" className="text-sm">Search terms</label>
        <Input id="refind-terms" ref={inputRef} value={terms} onChange={(e) => setTerms(e.target.value)}
          placeholder="e.g. HD 148937, magnetic merger" className="min-w-0 flex-[1_1_240px] bg-background" />
        <Button onClick={() => onFind(terms)} disabled={busy || same}>Find seeds again</Button>
      </div>
      {same && !busy && <p className="text-sm text-muted-foreground">Change the search terms first. The same terms find the same seeds.</p>}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {blame && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <span>
            <span className="font-medium text-foreground">{blame.label}</span>
            {` brought in ${blame.offTopic} of the ${blame.total} off-topic papers.`}
          </span>
          {onUntick && <Button variant="link" className="h-auto p-0 text-sm" onClick={onUntick}>Untick it</Button>}
        </p>
      )}
    </section>
  );
}
